import React, {useEffect, useMemo, useState} from "react";
import {Alert, Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message} from "antd";
import {DeleteOutlined, EditOutlined, MergeCellsOutlined, PlusOutlined} from "@ant-design/icons";
import {ApiFeaturesService} from "../../../../../shared/api";
import {SectionToolbar, countText, filterItems} from "../../components/SectionToolbar/SectionToolbar";
import {useCanEdit} from "../../../sectionAccess";

const SOURCES = [
  {value: 'eci_old', label: 'Со старого сайта'},
  {value: 'own', label: 'Свои метки портала'},
  {value: 'hidden', label: 'Скрытые'},
  {value: 'quick', label: 'Быстрые кнопки'},
]
const CODES = {apartments: 'фильтр «Апартаменты»', hit: 'ХИТ: первыми в списке'}
const errorOf = (error) => error?.response?.data?.errors || {}

// rename / group, or a new own tag
const NameModal = ({feature, onClose, onSaved}) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    let values
    try {
      values = await form.validateFields()
    } catch (e) {
      return
    }
    setSaving(true)
    try {
      const payload = {name: values.name, group: values.group || null}
      if (feature) await ApiFeaturesService.update(feature.id, payload)
      else await ApiFeaturesService.create(payload)
      message.success('Сохранено')
      onSaved()
    } catch (error) {
      const {message: text, field} = errorOf(error)
      if (field === 'name' || field === 'group') form.setFields([{name: field, errors: [text]}])
      else message.error(text || 'Не удалось сохранить')
    }
    setSaving(false)
  }

  return (
    <Modal visible title={feature ? feature.name : 'Новая метка портала'} okText='Сохранить' cancelText='Отмена' onOk={submit} confirmLoading={saving} onCancel={onClose} destroyOnClose>
      <Form form={form} layout='vertical' initialValues={{name: feature ? feature.name : '', group: feature ? feature.group || '' : ''}}>
        <Form.Item
          name='name'
          label='Название'
          rules={[{required: true, whitespace: true, message: 'Обязательное поле'}]}
          extra={feature && feature.eciOldId ? 'Название со старого сайта больше не придёт: остаётся это.' : null}
        >
          <Input maxLength={120}/>
        </Form.Item>
        <Form.Item name='group' label='Группа' extra='Например «Вид», «Планировка», «Двор» — для порядка в списке'>
          <Input maxLength={60}/>
        </Form.Item>
      </Form>
    </Modal>
  )
}

// move the flats of a feature to another one
const MergeModal = ({feature, features, onClose, onSaved}) => {
  const [into, setInto] = useState(undefined)
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!into) return message.info('Выберите, с чем объединить')
    setSaving(true)
    try {
      const {data} = await ApiFeaturesService.merge(feature.id, into)
      message.success(`Объединено, квартир: ${data.flats}`)
      onSaved()
    } catch (error) {
      message.error(errorOf(error).message || 'Не удалось объединить')
    }
    setSaving(false)
  }

  return (
    <Modal visible title={`Объединить «${feature.name}»`} okText='Объединить' cancelText='Отмена' onOk={submit} confirmLoading={saving} onCancel={onClose} destroyOnClose>
      <p>
        {feature.flats} кв. получат выбранную особенность вместо этой. «{feature.name}» пропадёт из списка;
        {feature.eciOldId ? ' если старый сайт снова пришлёт её, квартиры получат выбранную.' : ''}
      </p>
      <Select
        showSearch
        optionFilterProp='label'
        style={{width: '100%'}}
        placeholder='С какой особенностью объединить'
        value={into}
        onChange={setInto}
        options={features.filter((f) => f.id !== feature.id).map((f) => ({value: f.id, label: `${f.name} (${f.flats} кв.)`}))}
      />
    </Modal>
  )
}

// «Особенности»: every feature of flats — rename, group, hide from agents, quick buttons of the search, merge
export const FeaturesSection = () => {
  const canEdit = useCanEdit()
  const [features, setFeatures] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [source, setSource] = useState(undefined)
  const [modal, setModal] = useState(null)

  const load = () => {
    setLoading(true)
    ApiFeaturesService.fetchAll()
      .then(({data}) => {
        setFeatures(data)
        setError(null)
      })
      .catch(() => setError('Не удалось загрузить особенности. Обновите страницу.'))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const change = async (feature, values) => {
    try {
      await ApiFeaturesService.update(feature.id, values)
      load()
    } catch (e) {
      message.error(errorOf(e).message || 'Не удалось сохранить')
    }
  }

  const remove = async (feature) => {
    try {
      const {data} = await ApiFeaturesService.remove(feature.id)
      message.success(`Удалено${data.flats ? `, снято с ${data.flats} кв.` : ''}`)
      load()
    } catch (e) {
      message.error(errorOf(e).message || 'Не удалось удалить')
    }
  }

  const shown = useMemo(() => filterItems(features, search, ['name', 'group']).filter((f) => {
    if (source === 'eci_old') return Boolean(f.eciOldId)
    if (source === 'own') return f.own
    if (source === 'hidden') return f.hidden
    if (source === 'quick') return f.quick
    return true
  }), [features, search, source])

  const columns = [
    {
      title: 'Особенность',
      key: 'name',
      render: (f) => (
        <>
          <b>{f.name}</b>
          {f.code && <Tag color='purple' style={{marginLeft: 8}}>{CODES[f.code] || f.code}</Tag>}
          {f.merged && f.merged.length > 0 && <div className='cell-hint'>объединены: {f.merged.join(', ')}</div>}
        </>
      ),
    },
    {title: 'Группа', key: 'group', render: (f) => f.group || <span className='cell-empty'>—</span>},
    {
      title: 'Откуда',
      key: 'source',
      render: (f) => (f.own ? <Tag color='gold'>метка портала</Tag> : f.eciOldId ? <Tag color='blue'>старый сайт</Tag> : <Tag>нет на старом сайте</Tag>),
    },
    {
      title: 'Квартир',
      key: 'flats',
      align: 'right',
      sorter: (a, b) => a.flats - b.flats,
      render: (f) => <span style={{whiteSpace: 'nowrap'}}><b>{f.free}</b> своб. <span className='cell-hint'>из {f.flats}</span></span>,
    },
    {
      title: 'Видят агенты',
      key: 'hidden',
      render: (f) => <Switch checked={!f.hidden} disabled={!canEdit} onChange={(on) => change(f, {hidden: !on})}/>,
    },
    {
      title: 'Быстрая кнопка',
      key: 'quick',
      render: (f) => (
        <Space>
          <Switch checked={f.quick} disabled={f.hidden || !canEdit} onChange={(on) => change(f, {quick: on})}/>
          {f.quick && canEdit && (
            <InputNumber
              size='small'
              precision={0}
              style={{width: 64}}
              defaultValue={f.quickOrder}
              title='Порядок кнопок: меньше — левее'
              onBlur={(e) => {
                const n = Number(e.target.value)
                if (Number.isInteger(n) && n !== f.quickOrder) change(f, {quickOrder: n})
              }}
            />
          )}
        </Space>
      ),
    },
    canEdit && {
      title: '',
      key: 'actions',
      width: 150,
      render: (f) => (
        <Space>
          <Button icon={<EditOutlined/>} onClick={() => setModal({kind: 'name', feature: f})} title='Название и группа'/>
          <Button icon={<MergeCellsOutlined/>} onClick={() => setModal({kind: 'merge', feature: f})} title='Объединить с другой'/>
          {!f.eciOldId && !f.code && (
            <Popconfirm title={`Удалить «${f.name}»${f.flats ? ` и снять её с ${f.flats} кв.` : ''}?`} okText='Удалить' okButtonProps={{danger: true}} cancelText='Нет' onConfirm={() => remove(f)}>
              <Button danger icon={<DeleteOutlined/>} title='Удалить'/>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ].filter(Boolean)

  return (
    <>
      <Alert
        className='section-note'
        type='info'
        showIcon
        message='Особенности со старого сайта приходят вместе с квартирами: здесь им можно дать своё название и группу, скрыть от агентов, сделать быстрой кнопкой поиска или объединить с другой — старый сайт этого не изменит. Свои метки портала (ХИТ, Господдержка …) ставятся на квартиры в разделе «Квартиры» и синхронизацией не снимаются.'
      />
      {error && <Alert className='section-note' type='error' message={error}/>}
      <Table
        title={() => (
          <SectionToolbar
            search={search}
            onSearch={setSearch}
            placeholder='Название или группа'
            count={countText(features.length, shown.length, search)}
            filters={(
              <Select allowClear style={{width: 220}} placeholder='Все особенности' value={source} onChange={setSource} options={SOURCES}/>
            )}
          >
            {canEdit && <Button type='primary' icon={<PlusOutlined/>} onClick={() => setModal({kind: 'name', feature: null})}>Добавить метку</Button>}
          </SectionToolbar>
        )}
        columns={columns}
        dataSource={shown}
        rowKey='id'
        loading={loading}
        pagination={{pageSize: 50, showSizeChanger: false, hideOnSinglePage: true}}
        locale={{emptyText: 'Ничего не нашлось'}}
        sticky
        scroll={{x: 1000}}
      />
      {modal && modal.kind === 'name' && (
        <NameModal feature={modal.feature} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }}/>
      )}
      {modal && modal.kind === 'merge' && (
        <MergeModal feature={modal.feature} features={features} onClose={() => setModal(null)} onSaved={() => { setModal(null); load() }}/>
      )}
    </>
  )
}
